<template>
  <div class="main-collection critical-component-clear grid container">
    <div class="main-collection__content col xs-span l2-11">
      <!-- Collection Filters -->
      <filter-search-filters
        :active-filters="activeFilters"
        :filters="filterTree"
        :mobile-column-count="mobileColumnCount"
        :product-count="productCount"
        :sorting="localSorting"
        @filter-change="handleFilterChange"
        @filter-clear-all="handleClearAll"
        @filter-price-change="handleFilterPriceChange"
        @mobile-column-count-change="handleMobileColumnCountChange"
        @sort-change="handleSortChange"
      />

      <!-- Product Grid -->
      <filter-search-product-grid
        :active-filters="activeFilters"
        :loading="state === 'loading'"
        :mobile-column-count="mobileColumnCount"
        :products="localProducts"
        :collection-data="collection"
        :content-cards="contentCards"
      />

      <main-pagination
        v-if="pagination.parts.length"
        :pagination="pagination"
      />
    </div>

    <div
      v-if="collection.seoTitle && collection.seoText"
      class="main-collection__seo col xs-span l4-9 text-center"
    >
      <p
        class="text-heading-4xl"
        v-text="collection.seoTitle"
      />

      <collapsed-text
        :text="collection.seoText"
        :is-collapsed="isMobile"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Component to handle Collection filter and searching with Shopify Search and
 * Discovery.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Object} collection - Canvas formatted Collection data.
 *
 */

import { mapState } from 'vuex'
import { queryJSONTemplate } from '~/helpers/query-json'
import { unenscapeHTMLEntities } from '~/helpers/general'
import QueryCache from '~/helpers/query-caching'

import FilterSearchProductGrid from '~async/filter-search/filter-search-product-grid'
import FilterSearchFilters from '~async/filter-search/filter-search-filters'
import MainPagination from '~async/main-pagination/main-pagination'
import CollapsedText from '~async/collapsed-text/collapsed-text'

const queryCache = QueryCache({
  key: 'cnvs-collection-data',
})

export default {
  name: 'MainCollection',

  components: {
    FilterSearchFilters,
    FilterSearchProductGrid,
    MainPagination,
    CollapsedText,
  },

  props: {
    contentCards: {
      type: Array,
      default: () => [],
    },
    collection: {
      type: Object,
      default: () => ({
        description: '',
        price: 0,
        title: '',
        totalProducts: 0,
      }),
    },

    sorting: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      activeFilters: [],
      localFilters: this.collection.filters || [],
      localProducts: this.collection.products || [],
      localSorting: this.sorting,
      mobileColumnCount: Number(this.$variable('mobileProductGridColumns')),
      pagination: {
        startPage: 1,
        page: 1,
        hasNextPage: false,
        hasPreviousPage: false,
        parts: [],
        previousUrl: '',
        nextUrl: '',
      },
      productCount: this.collection.productsCount,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute an array of all the filter param names.
     * - Used to remove filter params when needed.
     */
    filterParamNames() {
      const paramNames = this.localFilters.map((filter) => {
        if (filter.type === 'price_range') {
          return [filter.minValue.paramName, filter.maxValue.paramName]
        }

        return filter.paramName
      }).flat()

      return paramNames
    },

    /**
     * Compute an array of all the filter param names.
     * - Used to remove filter params when needed.
     */
    filterTree() {
      if (!this.collection?.filterTree) {
        return this.localFilters
      }

      const filterTreeNames = new Set(this.collection.filterTree
        .map(filter => filter.filter_name))

      return this.localFilters.filter(filter => filterTreeNames.has(filter.label))
    },

    /**
     * Compute if there are active filters
     * @returns {boolean}
     */
    hasActiveFilters() {
      return this.filtersData.some(
        filter => filter.options.some(option => option.active),
      )
    },
  },

  mounted() {
    this.setInitialPagination(this.collection.pageInfo)
    this.setInitialActiveFilters(this.collection.filters)
    this.checkForCachedData()
  },

  methods: {

    /**
     * Query new collection data.
     * @param {Boolean} resetPaging - whether to reset all paging data
     */
    async queryCollection(resetPaging = false) {
      this.state = 'loading'

      if (resetPaging) {
        this.pagination.startPage = 1
        this.pagination.page = 1
        this.pagination.hasPreviousPage = false
        this.pagination.parts = []
        this.pagination.previousUrl = ''
        this.pagination.nextUrl = ''
      }

      const url = this.buildQueryParameters()

      const collection = await this.getCollectionWithCaching(url)

      /**
       * Update local product data.
       */
      this.localProducts = collection.products

      this.localFilters = collection.filters
      this.productCount = collection.productsCount
      this.setInitialPagination(collection.pageInfo)

      this.saveDataToCache({
        collection,
        products: this.localProducts,
        url,
      })

      // Update URL history state
      history.replaceState({ html: '' }, '', url)

      this.state = 'ready'
    },

    /**
     * Build URL for the query with updated query parameters.
     * @returns {String} formatted URL
     */
    buildQueryParameters() {
      const url = new URL(window.location.href)

      /**
       * Set page parameter.
       * - Removes the page parameter if the user is on the first page.
       */
      const pageToFetch = this.pagination.page

      if (pageToFetch === 1) {
        url.searchParams.delete('page')
      } else {
        url.searchParams.set('page', pageToFetch)
      }

      /**
       * Set sorting parameter.
       * - Removes the `sort_by` parameter if the user is on the default sorting
       *   order.
       */
      if (this.localSorting.current === this.localSorting.default) {
        url.searchParams.delete('sort_by')
      } else {
        url.searchParams.set('sort_by', this.localSorting.current)
      }

      /**
       * Set filter parameters.
       * - Clears all current filter parameters from the URL.
       * - Appends the new filter params to the URL.
       */
      [...url.searchParams].forEach(([key]) => {
        if (this.filterParamNames.includes(key)) {
          url.searchParams.delete(key)
        }
      })

      if (this.activeFilters.length) {
        this.activeFilters.forEach(activeFilter => {
          if (activeFilter.type === 'price_range') {
            if (activeFilter.minValue.value) {
              url.searchParams.set(
                activeFilter.minValue.paramName,
                activeFilter.minValue.value / 100,
              )
            } else {
              url.searchParams.delete(activeFilter.minValue.paramName)
            }

            if (activeFilter.maxValue.value) {
              url.searchParams.set(
                activeFilter.maxValue.paramName,
                activeFilter.maxValue.value / 100,
              )
            } else {
              url.searchParams.delete(activeFilter.maxValue.paramName)
            }

            return
          }

          const unescapedValue = unenscapeHTMLEntities(activeFilter.value)

          url.searchParams.append(activeFilter.paramName, unescapedValue)
        })
      }

      return url.toString()
    },

    /**
     * Handle saving the current collection data to the query cache.
     * - Only saves if the number of local products is more than the products
     *   per page setting.
     * - If there's 1 page worth of products or less, we can just load that
     *   directly from Liquid and don't need to use the query cache
     * - If we don't save to the query cache, we delete any existing query
     *   cache data to reduce the chance of out of date data.
     * @param {Object} payload - data used to save to the cache
     * @param {Object} collection - data from the API response
     * @param {Array} products - Array of products to save
     * @param {String} url - URL to cache
     */
    saveDataToCache({ collection, products, url }) {
      if (products.length > this.$variable('settings.pagination.products')) {
        const dataToCache = {
          url,
          data: {
            ...collection,
            products,
            pagination: this.pagination,
          },
        }

        queryCache.set(dataToCache)
      } else {
        queryCache.remove()
      }
    },

    /**
     * Fetch collection data with caching.
     * - If cached data exists, return that.
     * - Otherwise, make an API request.
     * @param {String} url - URL to fetch
     */
    getCollectionWithCaching(url) {
      const cachedData = this.getCachedData(url)

      if (cachedData?.data) {
        return cachedData.data
      }

      return this.getRemoteData(url)
    },

    /**
     * Fetch collection data with an API request
     * @param {String} url - URL to fetch
     */
    getRemoteData(url) {
      return queryJSONTemplate(url)
    },

    /**
     * Get data from the cache.
     * - Uses the `getWithValidation` method to validate that the cache data
     *   matches the current state.
     * @param {String} url - current URL
     */
    getCachedData(url) {
      const cachedData = queryCache.getWithValidation((cache) => {
        const isValid = cache.url === url
        return isValid
      })

      return cachedData
    },

    /**
     * Check if the current collection state has cached data and load that on
     * component mounted.
     * - If found, updates the local products and pagination state.
     */
    checkForCachedData() {
      const url = new URL(window.location)

      const cachedData = this.getCachedData(url.href)

      if (!cachedData) {
        return
      }

      this.localProducts = cachedData.data.products
      this.pagination = cachedData.data.pagination
    },

    /**
     * Handle filter value changes.
     * - If the filter value is in the `activeFilters` array, then remove it
     * - Otherwise add it to the array.
     * - Then trigger a collection query to fetch new collection data.
     * @param {Object} filterValue - Filter value object
     */
    handleFilterChange(filterValue) {
      const filterValueIndex = this.activeFilters.findIndex((activeFilter) => {
        return activeFilter.paramName === filterValue.paramName &&
          activeFilter.value === filterValue.value
      })

      if (filterValueIndex > -1) {
        this.activeFilters.splice(filterValueIndex, 1)
      } else {
        this.activeFilters.push(filterValue)
      }

      this.queryCollection(true)
    },

    handleFilterPriceChange(filterValue) {
      const filterValueIndex = this.activeFilters.findIndex((activeFilter) => {
        return activeFilter.paramName === filterValue.paramName
      })

      const isMinOrMaxSet = Boolean(filterValue.minValue.value || filterValue.maxValue.value)

      /**
       * If a min or max value is set for the price filter, then we add it to
       * the `activeFilters` array.
       * - If a price filter is already in the `activeFilters`, update the entry
       *   with splice, otherwise push the entry to the array.
       * - Otherwise, if neither min or max are set, remove the price filter
       *   from the active filters array.
       */
      if (isMinOrMaxSet) {
        if (filterValueIndex > -1) {
          this.activeFilters.splice(filterValueIndex, 1, filterValue)
        } else {
          this.activeFilters.push(filterValue)
        }

      } else {
        this.activeFilters.splice(filterValueIndex, 1)
      }

      this.queryCollection(true)
    },

    /**
     * Handle clearing all filters.
     * - Resets the `activeFilters` array and queries the collection.
     */
    handleClearAll() {
      this.activeFilters = []

      this.queryCollection(true)
    },

    /**
     * Handle mobile column change event.
     * - Takes the emitted column count and updates CSS variable and saves the
     *   value to local storage to persist the user's choice.
     * @param {Number} columnCount - selected column count.
     */
    handleMobileColumnCountChange(columnCount) {
      this.mobileColumnCount = columnCount
      document.documentElement.style.setProperty('--mobile-product-grid-columns', columnCount)
      window.localStorage?.setItem('--mobile-product-grid-columns', columnCount)
    },

    /**
     * Handle sort order change.
     * @param {String} sortOrder - new sort order id
     */
    handleSortChange(sortOrder) {
      this.localSorting.current = sortOrder

      this.queryCollection(true)
    },

    /**
     * Set initial pagination object from Liquid props.
     * @param {Object} pageInfo - formatted page information for the collection.
     */
    setInitialPagination(pageInfo) {
      if (!this.collection.pageInfo) {
        return
      }

      this.pagination = pageInfo
    },

    /**
     * Update the component active filters data with initial liquid filter data.
     * @param {Array} filters - Liquid filters data
     */
    setInitialActiveFilters(filters = []) {
      const initialActiveFilters = []

      filters.forEach((filter) => {
        if (filter.type === 'price_range' && (filter.minValue.value || filter.maxValue.value)) {
          initialActiveFilters.push(filter)

          return
        }

        if (filter.activeValues && filter.activeValues.length) {
          initialActiveFilters.push(...filter.activeValues)
        }
      })

      this.activeFilters = initialActiveFilters
    },

    /**
     * Dynamically determine if aspect ratio should be used for in grid banners.
     * If there is an adjacent product card, remove the ratio so that the card
     * heights are the same.
     * Only applies to single column cards on mobile.
     * @param card - The content card
     * @param index - The card's index in array
     * @returns {{aspectRatio: string}|{}}
     */
    getContentCardStyles(card, index) {
      const { mobile_col } = card

      // eslint-disable-next-line camelcase
      if (card.type !== 'content_card' || !this.isMobile || Number(mobile_col) !== 1) {
        return {}
      }

      const previousCard = this.allCards[index - 1] || {}
      const nextCard = this.allCards[index + 1] || {}
      const isEven = (index + 1) % 2 === 0

      const hasAdjacentProduct = (isEven && previousCard.type !== 'content_card') ||
        (!isEven && nextCard.type !== 'content_card')

      if (hasAdjacentProduct) {
        return {
          aspectRatio: 'unset',
        }
      }

      return {}
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
</style>
